if (!customElements.get('brand-menu')) {
  customElements.define(
    'brand-menu',
    class BrandMenu extends HTMLElement {
      connectedCallback() {
        this.previewPane = this.querySelector('[data-preview-pane]');
        this.brandLinks = this.querySelectorAll('.brand-menu__brand[data-has-preview="true"]');
        this.templates = this.querySelectorAll('template.brand-menu__template');
        this.details = this.closest('details');
        this.header = this.closest('.header-wrapper') || this.closest('header');

        this.brandSpeed = 450; // ms between one brand's product images
        this.idleSpeed = 1100; // ms between brands in the idle slideshow
        this.fadeMs = 400; // must match the CSS opacity transition
        this.interval = null;
        this.activeBrand = null;

        this._enter = (e) => this.showBrand(e.currentTarget);
        this._leave = () => this.startIdle();
        this.brandLinks.forEach((link) => {
          link.addEventListener('mouseenter', this._enter);
          link.addEventListener('focus', this._enter);
        });
        this.addEventListener('mouseleave', this._leave);

        // While the panel is open, a click outside it dismisses it. A click on page
        // content is also swallowed so the element underneath isn't activated; a click
        // elsewhere in the header (cart, search, another menu) is allowed to proceed.
        this._docClick = (e) => {
          if (!this.details || this.details.contains(e.target)) return;
          this.closeMenu();
          if (!(this.header && this.header.contains(e.target))) {
            e.preventDefault();
            e.stopPropagation();
          }
        };

        this._toggle = () => {
          if (this.details && this.details.open) {
            this.startIdle();
            document.addEventListener('click', this._docClick, true);
          } else {
            this.stopAll();
            document.removeEventListener('click', this._docClick, true);
          }
        };
        if (this.details) {
          this.details.addEventListener('toggle', this._toggle);
          if (this.details.open) {
            this.startIdle();
            document.addEventListener('click', this._docClick, true);
          }
        }
      }

      disconnectedCallback() {
        this.clearTimer();
        this.brandLinks?.forEach((link) => {
          link.removeEventListener('mouseenter', this._enter);
          link.removeEventListener('focus', this._enter);
        });
        this.removeEventListener('mouseleave', this._leave);
        if (this.details) this.details.removeEventListener('toggle', this._toggle);
        document.removeEventListener('click', this._docClick, true);
      }

      closeMenu() {
        if (!this.details) return;
        this.details.removeAttribute('open');
        const summary = this.details.querySelector('summary');
        if (summary) summary.setAttribute('aria-expanded', 'false');
      }

      clearTimer() {
        if (this.interval) {
          clearInterval(this.interval);
          this.interval = null;
        }
      }

      // Crossfade one image into the pane; keeps at most ~2 nodes in flight.
      crossfade(sourceImg) {
        if (!sourceImg || !this.previewPane) return;
        const incoming = sourceImg.cloneNode(true);
        const outgoing = this.previewPane.lastElementChild;
        this.previewPane.appendChild(incoming);
        requestAnimationFrame(() => incoming.classList.add('active'));
        if (outgoing) {
          outgoing.classList.remove('active');
          setTimeout(() => outgoing.remove(), this.fadeMs);
        }
      }

      // Idle "attract" slideshow: the first product image of each brand, in turn.
      // Runs only while the panel is open; loads one image per tick.
      startIdle() {
        this.clearTimer();
        this.activeBrand = null;
        this.brandLinks.forEach((l) => l.classList.remove('brand-menu__brand--active'));
        if (!this.previewPane) return;

        const firsts = Array.from(this.templates)
          .map((t) => t.content.querySelector('.brand-menu__product'))
          .filter(Boolean);
        if (!firsts.length) return;

        const first = firsts[0].cloneNode(true);
        this.previewPane.replaceChildren(first);
        requestAnimationFrame(() => first.classList.add('active'));

        if (firsts.length < 2) return;
        let i = 1;
        this.interval = setInterval(() => {
          this.crossfade(firsts[i % firsts.length]);
          i += 1;
        }, this.idleSpeed);
      }

      // Hovered/focused brand: cycle through that brand's own product images.
      showBrand(link) {
        const brand = link.dataset.brand;
        if (!brand || !this.previewPane || this.activeBrand === brand) return;
        const sel = `template[data-brand="${window.CSS && CSS.escape ? CSS.escape(brand) : brand}"]`;
        const tpl = this.querySelector(sel);
        if (!tpl) return;

        this.clearTimer();
        this.activeBrand = brand;
        this.brandLinks.forEach((l) => l.classList.toggle('brand-menu__brand--active', l === link));

        this.previewPane.replaceChildren(tpl.content.cloneNode(true));
        const images = this.previewPane.querySelectorAll('.brand-menu__product');
        if (!images.length) {
          this.activeBrand = null;
          return;
        }
        let i = 0;
        images[0].classList.add('active');
        if (images.length > 1) {
          this.interval = setInterval(() => {
            images[i].classList.remove('active');
            i = (i + 1) % images.length;
            images[i].classList.add('active');
          }, this.brandSpeed);
        }
      }

      stopAll() {
        this.clearTimer();
        this.activeBrand = null;
        this.brandLinks.forEach((l) => l.classList.remove('brand-menu__brand--active'));
        if (this.previewPane) this.previewPane.replaceChildren();
      }
    }
  );
}
